/**
 * SoleadLab 2.0 — Observation Store
 *
 * B3.2: Zustand store for observation entity CRUD.
 *
 * Observations are mutable — updates use optimistic concurrency (If-Match).
 * Site-scoped: call setSiteId to switch sites, which clears stale items.
 *
 * State:
 *   items     — cached observations for the current site
 *   loading   — fetch in progress
 *   error     — last error message (null if none)
 *   siteId    — current site scope
 *
 * Actions:
 *   setSiteId(id)             — switch site scope (clears + refetches)
 *   fetchItems(filters?)      — load observations for current site
 *   getById(id)               — lookup a cached item by ID
 *   create(obs)               — create a new observation
 *   update(id, updates, ver)  — update with optimistic concurrency
 *   clear()                   — reset store state
 */

import { create } from 'zustand';
import {
  fetchObservations,
  createObservation,
  updateObservation,
  isConflictError,
  type Observation,
  type ObservationFilters,
} from '../services/observationApi';

interface ObservationState {
  items: Observation[];
  loading: boolean;
  error: string | null;
  siteId: string | null;

  setSiteId: (siteId: string) => void;
  fetchItems: (filters?: ObservationFilters) => Promise<void>;
  getById: (id: string) => Observation | undefined;
  create: (
    obs: Parameters<typeof createObservation>[1],
  ) => Promise<Observation>;
  update: (
    id: string,
    updates: Parameters<typeof updateObservation>[2],
    version: number,
  ) => Promise<Observation>;
  clear: () => void;
}

export const useObservationStore = create<ObservationState>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  siteId: null,

  setSiteId: (siteId: string) => {
    const current = get().siteId;
    if (current === siteId) return;
    set({ siteId, items: [], error: null });
  },

  fetchItems: async (filters?: ObservationFilters) => {
    const { siteId } = get();
    if (!siteId) return;

    set({ loading: true, error: null });
    try {
      const result = await fetchObservations(siteId, filters);
      set({ items: result.items, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch observations',
      });
    }
  },

  getById: (id: string) => {
    return get().items.find((item) => item.id === id);
  },

  create: async (obs) => {
    const { siteId } = get();
    if (!siteId) throw new Error('No site selected');

    const created = await createObservation(siteId, obs);
    set({ items: [...get().items, created] });
    return created;
  },

  update: async (id, updates, version) => {
    const { siteId } = get();
    if (!siteId) throw new Error('No site selected');

    try {
      const updated = await updateObservation(siteId, id, updates, version);
      set({
        items: get().items.map((item) =>
          item.id === id ? updated : item,
        ),
      });
      return updated;
    } catch (err) {
      if (isConflictError(err)) {
        set({ error: 'Version conflict — another update occurred. Please refresh.' });
      }
      throw err;
    }
  },

  clear: () => {
    set({ items: [], siteId: null, error: null, loading: false });
  },
}));
